import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import type { Digest } from "@backstory/protocol";
import { useCan } from "../../../auth/roles";
import { ErrorState, Skeleton } from "../../../components/States";
import { AutoLoadMore, ConfirmDialog, PageHeader, RowMenu, useToast } from "../../../components/ui";
import { formatDateTime } from "../../../lib/format";
import { aiApi, aiKeys } from "../api";
import { AiOutput, ArtifactMetaLine } from "../components/common";
import { useAiCapability } from "../hooks";
import "../ai.css";

function useDigests(enabled = true) {
  return useInfiniteQuery({
    queryKey: aiKeys.digests(),
    queryFn: ({ pageParam }) => aiApi.digests(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: false,
  });
}

function DigestSkeletonCards({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="digest digest-skeleton" aria-hidden="true">
          <span className="skeleton" style={{ width: "28%", height: 10 }}>Loading</span>
          <span className="skeleton" style={{ width: "72%", height: 16 }}>Loading digest</span>
          <span className="skeleton" style={{ width: "20%", height: 12 }}>Loading</span>
        </div>
      ))}
    </>
  );
}

function DigestMenu({ d, onDelete }: { d: Digest; onDelete: (d: Digest) => void }) {
  const can = useCan();
  if (!can.admin) return null;
  return (
    <RowMenu label={`Actions for ${d.headline}`}>
      <button type="button" role="menuitem" className="danger" onClick={() => onDelete(d)}>Delete</button>
    </RowMenu>
  );
}

function useDigestDelete(leave: boolean) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState<Digest | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => aiApi.deleteDigest(id),
    onSuccess: () => {
      toast.push("success", "Digest deleted");
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: aiKeys.digests() });
      if (leave) void navigate({ to: "/ai/digests" });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const dialog = (
    <ConfirmDialog
      open={!!removing}
      danger
      title="Delete digest"
      confirmLabel="Delete digest"
      busy={del.isPending}
      onClose={() => { if (!del.isPending) setRemoving(null); }}
      onConfirm={() => removing && del.mutate(removing.id)}
    >
      <p style={{ margin: 0 }}>
        <strong>{removing?.headline}</strong> is removed from Digests. A copy already sent to a channel stays sent.
      </p>
    </ConfirmDialog>
  );
  return { setRemoving, dialog };
}

export function DigestView({ d, full, onDelete }: { d: Digest; full: boolean; onDelete?: (d: Digest) => void }) {
  return (
    <AiOutput className="digest" testId="digest">
      {!full && onDelete && (
        <div className="digest-actions">
          <DigestMenu d={d} onDelete={onDelete} />
        </div>
      )}
      <div className="period">{d.period} · {formatDateTime(d.from)} → {formatDateTime(d.to)}</div>
      <h3>{full ? d.headline : <Link to="/ai/digests/$id" params={{ id: d.id }} className="digest-hit">{d.headline}</Link>}</h3>
      {full &&
        d.sections.map((s, i) => (
          <section key={i}>
            <h4>{s.title}</h4>
            <p className="small" style={{ margin: 0, whiteSpace: "pre-wrap" }}>{s.text}</p>
            {s.links.length > 0 && (
              <div className="links">
                {s.links.map((l) => (
                  <a key={l.url} className="badge neutral" href={l.url}>{l.label}</a>
                ))}
              </div>
            )}
          </section>
        ))}
      {!full && <p className="small muted" style={{ margin: "4px 0 0" }}>{d.sections.length} sections</p>}
      {full && <ArtifactMetaLine meta={d.meta} />}
    </AiOutput>
  );
}

export function DigestsPage() {
  const ai = useAiCapability();
  const q = useDigests();
  const remove = useDigestDelete(false);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <>
      <PageHeader title="Digests" />
      {q.isPending && <DigestSkeletonCards count={4} />}
      {q.isError && <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>}
      {q.isSuccess && items.length === 0 && (ai.blocked ? (
        <div className="card section empty" data-testid="digests-unavailable">
          <h3>{ai.hint}</h3>
          <p className="muted">{ai.detail || "Digests are written by AI, so new ones appear once AI is available."}</p>
        </div>
      ) : (
        <div className="card section empty">
          <h3>No digests yet</h3>
          <p className="muted">Digests are generated once there is a day of recorded sessions. Enable them in AI settings.</p>
        </div>
      ))}
      <div className="digest-list" aria-busy={q.isPending || q.isFetchingNextPage}>
        {items.map((d) => (
          <DigestView key={d.id} d={d} full={false} onDelete={remove.setRemoving} />
        ))}
        {q.isFetchingNextPage && <DigestSkeletonCards count={2} />}
      </div>
      <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more digests" />
      {remove.dialog}
    </>
  );
}

export function DigestDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const can = useCan();
  const q = useDigests();
  const remove = useDigestDelete(true);
  const d = q.data?.pages.flatMap((p) => p.items).find((x) => x.id === id);
  if (q.isPending) return <Skeleton lines={8} />;
  if (q.isError) return <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>;
  if (!d && q.hasNextPage) {
    return (
      <>
        <PageHeader back={<Link to="/ai/digests" className="btn ghost">← Digests</Link>} title="Loading digest…" />
        <AutoLoadMore hasMore loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading digest" />
      </>
    );
  }
  if (!d) {
    return (
      <>
        <PageHeader back={<Link to="/ai/digests" className="btn ghost">← Digests</Link>} title="Digest not found" />
        <p className="muted">No digest with that id was found.</p>
      </>
    );
  }
  return (
    <>
      <PageHeader
        back={<Link to="/ai/digests" className="btn ghost">← Digests</Link>}
        title={d.headline}
        subtitle={`${d.period} digest`}
        actions={can.admin ? <DigestMenu d={d} onDelete={remove.setRemoving} /> : undefined}
      />
      <DigestView d={d} full />
      {remove.dialog}
    </>
  );
}
